import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { z } from "zod";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Divider,
  Avatar,
  Select,
  SelectItem,
} from "@heroui/react";
import {
  ArrowLeft,
  User,
  Mail,
  Calendar,
  Clock,
  Shield,
  UserCheck,
  Activity,
  BarChart3,
  TrendingUp,
} from "lucide-react";
import { getMember, kickMember } from "@/api/user";
import {
  getDailyUsageStatsByMember,
  getMonthlyUsageStatsByMember,
} from "@/api/stats";
import type { Member } from "@/api/user.type";
import type { UsageStats } from "@/api/stats.type";
import dayjs from "dayjs";
import { useHttpRequest } from "@/hooks/useHttpRequest";
import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const paramsSchema = z.object({
  id: z.string().refine((val) => !isNaN(Number(val)), {
    message: "유효하지 않은 사용자 ID입니다.",
  }),
});

export const Route = createFileRoute("/(authenticated)/users/$id")({
  params: {
    parse: paramsSchema.parse,
    stringify: ({ id }) => ({ id }),
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const router = useRouter();
  const { id } = Route.useParams();
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  const {
    data: memberResponse,
    error,
    isLoading,
    request: requestMember,
  } = useHttpRequest(() => getMember(Number(id)));

  const {
    data: monthlyStatsResponse,
    isLoading: isMonthlyStatsLoading,
    request: requestMonthlyStats,
  } = useHttpRequest(() =>
    getMonthlyUsageStatsByMember(Number(id), selectedYear)
  );

  const {
    data: dailyStatsResponse,
    isLoading: isDailyStatsLoading,
    request: requestDailyStats,
  } = useHttpRequest(() =>
    getDailyUsageStatsByMember(Number(id), selectedMonth, selectedYear)
  );

  const member = memberResponse?.data;

  const handleGoBack = () => {
    navigate({
      to: "/users",
    });
  };

  const getRoleIcon = (role: Member["role"]) => {
    switch (role) {
      case "ADMIN":
        return <Shield className="h-3 w-3" />;
      case "MANAGER":
        return <UserCheck className="h-3 w-3" />;
      case "MEMBER":
        return <User className="h-3 w-3" />;
      default:
        return <User className="h-3 w-3" />;
    }
  };

  const getRoleDescription = (role: Member["role"]) => {
    switch (role) {
      case "ADMIN":
        return "모든 권한을 가진 관리자입니다.";
      case "MANAGER":
        return "멤버 관리 권한을 가진 매니저입니다.";
      case "MEMBER":
        return "일반 멤버입니다.";
      default:
        return "권한이 설정되지 않았습니다.";
    }
  };

  const formatChartData = (data: UsageStats[]) => {
    return data.map((item) => ({
      date: dayjs(item.date).format("MM/DD"),
      count: item.usages.reduce(
        (sum, usage) =>
          sum + usage.successCount + usage.failCount + usage.timeoutCount,
        0
      ),
      fullDate: dayjs(item.date).format("YYYY-MM-DD"),
    }));
  };

  const formatMonthlyChartData = (data: UsageStats[]) => {
    return data.map((item) => ({
      date: dayjs(item.date).format("MM월"),
      count: item.usages.reduce(
        (sum, usage) =>
          sum + usage.successCount + usage.failCount + usage.timeoutCount,
        0
      ),
      fullDate: dayjs(item.date).format("YYYY-MM"),
    }));
  };

  const generateMonthOptions = () => {
    const months = [];
    for (let i = 1; i <= 12; i++) {
      months.push({
        key: i.toString(),
        label: `${i}월`,
      });
    }
    return months;
  };

  const generateYearOptions = () => {
    const years = [];
    const currentYear = dayjs().year();
    for (let i = currentYear - 2; i <= currentYear; i++) {
      years.push({
        key: i.toString(),
        label: `${i}년`,
      });
    }
    return years;
  };

  const handleKickMember = () => {
    const result = confirm("정말 이 멤버를 내보낼까요?");
    if (result) {
      kickMember(Number(id))
        .then(() => router.history.back())
        .catch(console.error);
    }
  };

  const dailyTotalUsageCount = dailyStatsResponse?.data?.reduce(
    (sum, item) =>
      sum +
      item.usages.reduce(
        (sum, usage) =>
          sum + usage.successCount + usage.failCount + usage.timeoutCount,
        0
      ),
    0
  );

  const monthlyTotalUsageCount = monthlyStatsResponse?.data?.reduce(
    (sum, item) =>
      sum +
      item.usages.reduce(
        (sum, usage) =>
          sum + usage.successCount + usage.failCount + usage.timeoutCount,
        0
      ),
    0
  );

  useEffect(() => {
    requestMember();
  }, [id]);

  useEffect(() => {
    if (id) {
      requestDailyStats();
    }
  }, [id, selectedMonth]);

  useEffect(() => {
    if (id) {
      requestMonthlyStats();
    }
  }, [id, selectedYear]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-default-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Card className="max-w-md mx-auto" shadow="sm">
            <CardBody className="text-center space-y-4 p-6">
              <div className="text-danger">
                사용자 정보를 불러오는 중 오류가 발생했습니다.
              </div>
              <Button
                onClick={handleGoBack}
                variant="bordered"
                startContent={<ArrowLeft className="h-4 w-4" />}
              >
                목록으로 돌아가기
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-default-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-default-500">로딩 중...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-default-50 p-6">
        <div className="max-w-7xl mx-auto">
          <Card className="max-w-md mx-auto" shadow="sm">
            <CardBody className="text-center space-y-4 p-6">
              <div className="text-foreground-500">
                해당 사용자를 찾을 수 없습니다.
              </div>
              <Button
                onClick={handleGoBack}
                variant="bordered"
                startContent={<ArrowLeft className="h-4 w-4" />}
              >
                목록으로 돌아가기
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-default-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleGoBack}
            variant="light"
            size="sm"
            startContent={<ArrowLeft className="h-4 w-4" />}
          >
            멤버 목록
          </Button>
          <Divider orientation="vertical" className="h-6" />
          <h1 className="text-2xl font-semibold text-foreground">
            멤버 상세 정보
          </h1>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card shadow="sm">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-primary" />
                <span className="font-semibold text-lg">기본 정보</span>
              </div>
            </CardHeader>
            <CardBody className="space-y-6 p-6 pt-0">
              <div className="flex items-center space-x-4">
                <Avatar
                  src={member.profile}
                  name={member.name}
                  size="lg"
                  className="flex-shrink-0"
                  showFallback
                  fallback={<User className="h-8 w-8" />}
                />
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold">{member.name}</h2>
                  <p className="text-default-500 text-sm">ID: {member.id}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="h-4 w-4 text-default-500 mt-1" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">이메일</p>
                    <p className="text-sm text-default-600 break-all">
                      {member.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Shield className="h-4 w-4 text-default-500 mt-1" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">역할</p>
                    <Chip
                      variant="flat"
                      startContent={getRoleIcon(member.role)}
                      size="sm"
                    >
                      {member.role}
                    </Chip>
                    <p className="text-xs text-default-500">
                      {getRoleDescription(member.role)}
                    </p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card shadow="sm">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center space-x-2">
                <Activity className="h-5 w-5 text-secondary" />
                <span className="font-semibold text-lg">활동 정보</span>
              </div>
            </CardHeader>
            <CardBody className="space-y-4 p-6 pt-0">
              <div className="flex items-start space-x-3">
                <Calendar className="h-4 w-4 text-default-500 mt-1" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">가입일</p>
                  <p className="text-sm text-default-600">
                    {dayjs(member.joinAt).format("YYYY년 MM월 DD일 HH:mm")}
                  </p>
                  <Chip
                    size="sm"
                    variant="flat"
                    color="success"
                    className="text-xs"
                  >
                    가입한 지 {dayjs().diff(dayjs(member.joinAt), "day")}일 경과
                  </Chip>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Clock className="h-4 w-4 text-default-500 mt-1" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">최근 로그인</p>
                  {member.lastLoginAt ? (
                    <>
                      <p className="text-sm text-default-600">
                        {dayjs(member.lastLoginAt).format(
                          "YYYY년 MM월 DD일 HH:mm"
                        )}
                      </p>
                      <Chip
                        size="sm"
                        variant="flat"
                        color="warning"
                        className="text-xs"
                      >
                        {dayjs(member.lastLoginAt).fromNow()}
                      </Chip>
                    </>
                  ) : (
                    <p className="text-sm text-default-500">
                      로그인 기록이 없습니다.
                    </p>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card shadow="sm">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-lg">월별 사용량</span>
                </div>
                <Select
                  size="sm"
                  placeholder="년도 선택"
                  className="w-28 ml-2"
                  selectedKeys={[selectedYear.toString()]}
                  onSelectionChange={(keys) => {
                    const yearArray = Array.from(keys);
                    if (yearArray.length > 0) {
                      setSelectedYear(Number(yearArray[0]));
                    }
                  }}
                >
                  {generateYearOptions().map((year) => (
                    <SelectItem key={year.key}>{year.label}</SelectItem>
                  ))}
                </Select>
              </div>
            </CardHeader>
            <CardBody className="p-6 pt-0">
              {isMonthlyStatsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-default-500">로딩 중...</p>
                  </div>
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    className="border border-default-100 rounded-md px-2 py-1"
                  >
                    <AreaChart
                      data={formatMonthlyChartData(
                        monthlyStatsResponse?.data || []
                      )}
                      margin={{
                        top: 10,
                        right: 20,
                        left: 20,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        className="opacity-30"
                      />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--heroui-default-500))"
                        fontSize={10}
                        axisLine={{ className: "opacity-30" }}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--heroui-content1))",
                          border: "1px solid hsl(var(--heroui-default-200))",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                          padding: "6px",
                          fontSize: "13px",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="hsl(var(--heroui-primary))"
                        fill="hsl(var(--heroui-primary))"
                        fillOpacity={0.1}
                        strokeWidth={2}
                        dot={{
                          fill: "hsl(var(--heroui-secondary))",
                          strokeWidth: 2,
                          r: 3,
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-4 flex items-center justify-between text-sm text-default-500">
                <span>총 사용량</span>
                <div className="font-semibold">
                  <span className="text-2xl font-bold text-foreground">
                    {monthlyTotalUsageCount ?? 0}
                  </span>

                  <span className="ml-1">회</span>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card shadow="sm">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-secondary" />
                  <span className="font-semibold text-lg">일별 사용량</span>
                </div>
                <Select
                  size="sm"
                  placeholder="월 선택"
                  className="w-24 ml-2"
                  selectedKeys={[selectedMonth.toString()]}
                  onSelectionChange={(keys) => {
                    const monthArray = Array.from(keys);
                    if (monthArray.length > 0) {
                      setSelectedMonth(Number(monthArray[0]));
                    }
                  }}
                >
                  {generateMonthOptions().map((month) => (
                    <SelectItem key={month.key}>{month.label}</SelectItem>
                  ))}
                </Select>
              </div>
            </CardHeader>
            <CardBody className="p-6 pt-0">
              {isDailyStatsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-default-500">로딩 중...</p>
                  </div>
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    className="border border-default-100 rounded-md px-2 py-1"
                  >
                    <LineChart
                      data={formatChartData(dailyStatsResponse?.data || [])}
                      margin={{
                        top: 10,
                        right: 20,
                        left: 20,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        className="opacity-30"
                      />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--heroui-default-500))"
                        fontSize={10}
                        axisLine={{ className: "opacity-30" }}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--heroui-content1))",
                          border: "1px solid hsl(var(--heroui-default-200))",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                          padding: "6px",
                          fontSize: "13px",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="hsl(var(--heroui-secondary))"
                        strokeWidth={2}
                        dot={{
                          fill: "hsl(var(--heroui-secondary))",
                          strokeWidth: 2,
                          r: 3,
                        }}
                        activeDot={{
                          r: 5,
                          stroke: "hsl(var(--heroui-secondary))",
                          strokeWidth: 2,
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-4 flex items-center justify-between text-sm text-default-500">
                <span>이번 달 사용량</span>
                <div className="font-semibold">
                  <span className="text-2xl font-bold text-foreground">
                    {dailyTotalUsageCount ?? 0}
                  </span>

                  <span className="ml-1">회</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <Card shadow="sm">
          <CardHeader className="p-6 pb-3">
            <h3 className="font-semibold text-lg">관리 액션</h3>
          </CardHeader>
          <CardBody className="p-6 pt-0">
            <div className="flex flex-wrap gap-3">
              <Button
                color="danger"
                variant="bordered"
                onPress={handleKickMember}
                size="sm"
              >
                멤버 내보내기
              </Button>
            </div>
            <p className="text-xs text-default-400 mt-3"></p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
